import {
	Box,
	Button,
	DateTimePicker,
	H2,
	HStack,
	Input,
	Label,
	RadioButtonList,
	Select,
	SelectOptionType,
	Text,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import { BUNDLES_CREATE_BUNDLE, BUNDLES_UPDATE_BUNDLE } from 'apiRoutes';
import BackButton from 'components/common/BackButton/BackButton';
import SectionDivider from 'components/common/SectionDivider';
import { AreaCheckboxTree } from 'pages/Subscription/SubscriptionForm/PermissionsForm/AreaCheckboxTree/AreaCheckboxTree';
import {
	SubscriptionStatus,
	SubscriptionType,
} from 'domain/subscription/interfaces/Subscription';
import useSubmit, { POST } from 'hooks/useSubmit';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';
import moment, { Moment } from 'moment';
import { getTypeOptions } from 'pages/Subscription/SubscriptionForm/options';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { useBundleFormContext } from './BundleFormContext';
import { extractLeafNodes } from 'utils/geoNodeHelpers';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import { FunctionComponent } from 'react';

const BundleForm: FunctionComponent<{ bundleId?: number }> = ({
	bundleId = null,
}) => {
	const { getAreaByCode } = useGlobalContext();
	const {
		availableApplications,
		availableAreas,
		availableAddons,
		bundleFormState,
		setBundleFormState,
	} = useBundleFormContext();
	const navigate = useNavigate();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const isEdit = bundleId !== null;

	const [submitFormData, submitFormStatus] = useSubmit(
		isEdit ? BUNDLES_UPDATE_BUNDLE : BUNDLES_CREATE_BUNDLE,
		POST,
		{
			messageOnError: 'An error occurred, please try again!',
			messageOnSuccess: isEdit ? 'Bundle updated' : 'Bundle created',
			showApiError: true,
		},
	);

	const onTypeSelectionChanged = (type: SubscriptionType) => {
		setBundleFormState({
			...bundleFormState,
			type,
		});
	};

	const onDescriptionChanged = (description: string) => {
		setBundleFormState({
			...bundleFormState,
			description,
		});
	};

	const onEndDateChange = (newValue: Moment) => {
		setBundleFormState({
			...bundleFormState,
			endDate: newValue.utc().startOf('day'),
		});
	};

	const isValidEndDate = (current: Moment) =>
		!current.isBefore(moment().add(1, 'day'), 'day');

	const onSubmit = async () => {
		const payLoad: any = {
			productId: bundleFormState.productId,
			ownerId: bundleFormState.companyId ?? null,
			endDate: bundleFormState.endDate,
			seats: Number(bundleFormState.seats),
			type: bundleFormState.type,
			description: bundleFormState.description,
			status: SubscriptionStatus.Active,
			permissions: {
				countries: bundleFormState.areas
					.map((area) =>
						getAreaByCode(bundleFormState.productId, area),
					)
					.filter((value) => value !== null && value !== undefined)
					.flatMap(extractLeafNodes)
					.map((country) => country.code),
				applicationPermissions: [
					{
						applicationId: bundleFormState.productId,
						addons: bundleFormState.addons,
					},
				],
			},
		};

		if (isEdit) {
			payLoad.bundleId = bundleId;
		}

		const result = await submitFormData(payLoad);

		if (result.success) {
			navigate(-1);
		}
	};

	const canSubmit =
		bundleFormState.productId &&
		bundleFormState.seats > 0 &&
		bundleFormState.type;

	return (
		<Box bg="bgMainContainer">
			<BackButton />
			<FormContainer bg="bgMainContainer">
				<VStack gap="md">
					<H2>Bundle Details</H2>
					<RadioButtonList
						label=""
						hideLabel
						required
						options={getTypeOptions()}
						onChange={onTypeSelectionChanged}
						value={bundleFormState.type}
					/>
					<Select
						label="Product"
						options={availableApplications}
						required
						onChange={(selectedApplication: SelectOptionType) =>
							setBundleFormState({
								...bundleFormState,
								productId: selectedApplication.value,
								areas: [],
								addons: [],
							})
						}
						value={
							availableApplications.find(
								(a) => a.value === bundleFormState.productId,
							) ?? null
						}
					/>
					<Input
						label="Description"
						type="text"
						name="description"
						placeholder="Enter description"
						value={bundleFormState.description}
						onChange={(event) =>
							onDescriptionChanged(event.target.value)
						}
					/>
					<HStack fullWidth gap="md" wrap="nowrap">
						<VStack fullWidth wrap="nowrap" align="flex-start">
							<DateTimePicker
								label="End Date"
								keepLabelHeightWhenHidden
								utc
								isValidDate={(current: Moment) =>
									isValidEndDate(current)
								}
								initialViewDate={bundleFormState.endDate}
								onChange={onEndDateChange}
								value={bundleFormState.endDate}
								required={false}
								readonly
							/>
							{bundleFormState.endDate && (
								<HStack fullWidth justify="flex-end">
									<Button
										onClick={() =>
											setBundleFormState({
												...bundleFormState,
												endDate: undefined,
											})
										}
										variant="tertiary"
										text="Clear end date"
										size="sm"
									/>
								</HStack>
							)}
						</VStack>
						<Input
							type="number"
							label="Seats"
							name="seats"
							value={bundleFormState.seats}
							required
							onChange={(event) =>
								setBundleFormState({
									...bundleFormState,
									seats: event.target.value,
								})
							}
						/>
					</HStack>
				</VStack>
				<SectionDivider />
				<VStack gap="xl">
					<H2>Permissions</H2>
					{!bundleFormState.productId && (
						<Text size="sm" fontStyle="italic">
							Select a product to set permissions for the bundle
						</Text>
					)}
				</VStack>
				<VStack>
					{availableAddons.length > 0 && (
						<VStack gap="xs" fullWidth>
							<Select
								label="Addons"
								options={availableAddons}
								isMulti
								onChange={(
									selectedAddons: SelectOptionType[],
								) => {
									console.log(selectedAddons);
									setBundleFormState({
										...bundleFormState,
										addons: selectedAddons.map(
											(addon) => addon.value,
										),
									});
								}}
								value={availableAddons.filter((addon) =>
									bundleFormState.addons.includes(
										addon.value,
									),
								)}
								variant="checkbox"
							/>
						</VStack>
					)}
					{availableAreas.length > 0 && (
						<VStack gap="xs" fullWidth>
							<Label text="Areas" />
							<AreaCheckboxTree
								initialState={bundleFormState.areas}
								availableAreas={availableAreas}
								onChange={(countries: string[]) =>
									setBundleFormState({
										...bundleFormState,
										areas: countries,
									})
								}
							/>
						</VStack>
					)}
				</VStack>
				<SectionDivider />
				<HStack fullWidth justify="flex-end">
					<Button
						text="Save bundle"
						isDisabled={
							!canSubmit || isRestrictedToRole(AdminRole.SALES)
						}
						onClick={onSubmit}
						type="submit"
						loadingText="Submitting"
						isLoading={submitFormStatus === RequestStatus.LOADING}
					/>
				</HStack>
			</FormContainer>
		</Box>
	);
};

const FormContainer = styled(Box)`
	max-width: 600px;
`;

export default BundleForm;
